import { Component, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { finalize, map, of, switchMap } from 'rxjs';
import { ParticipantDomainService } from '../domain-services/participant-domain.service';
import { ProjectDomainService } from '../domain-services/project-domain.service';

@Component({
  selector: 'app-invite-participants',
  imports: [MatInputModule, MatButtonModule, RouterLink],
  templateUrl: './invite-participants.html',
  styleUrl: './invite-participants.scss',
})
export class InviteParticipants {
  private readonly participantDomain = inject(ParticipantDomainService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly projectDomain = inject(ProjectDomainService);

  emailsText = signal('');
  isSubmitting = signal(false);
  apiError = signal<string | null>(null);

  errors = computed(() => {
    const raw = this.emailsText();
    const trimmed = raw.trim();

    // No error on initial empty/whitespace input
    if (!trimmed) {
      return [];
    }

    const parts = raw.split(',');

    let hasEmptyEntry = false;
    const validEmails: string[] = [];
    const invalid: string[] = [];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    for (const part of parts) {
      const email = part.trim();
      if (!email) {
        hasEmptyEntry = true;
        continue;
      }
      if (!emailRegex.test(email)) {
        invalid.push(email);
      } else {
        validEmails.push(email);
      }
    }

    if (hasEmptyEntry || invalid.length > 0) {
      if (invalid.length > 0) {
        return [
          `The following address${invalid.length > 1 ? 'es are' : ' is'} invalid: ${invalid.join(
            ', ',
          )}`,
        ];
      }
      return ['There are empty or whitespace-only entries in the list.'];
    }

    return [];
  });

  onEmailsInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement | null;
    if (!target) return;
    this.emailsText.set(target.value);
  }

  onAddClick(): void {
    if (!this.emailsText().trim() || this.errors().length > 0 || this.isSubmitting()) {
      return;
    }

    const project = this.projectDomain.currentProject();
    const projectId = project?.id;
    if (!projectId) {
      this.apiError.set('Missing project id.');
      return;
    }

    this.isSubmitting.set(true);
    this.apiError.set(null);

    const raw = this.emailsText();
    const parts = raw.split(',');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const candidateEmails: string[] = [];

    for (const part of parts) {
      const email = part.trim();
      if (email && emailRegex.test(email)) {
        candidateEmails.push(email);
      }
    }

    const distinctCandidates = Array.from(new Set(candidateEmails.map((e) => e.toLowerCase())));

    this.participantDomain
      .loadProjectParticipants(projectId)
      .pipe(
        map((participants) => {
          const existing = new Set(
            participants.map((p) => p.email?.toLowerCase()).filter((e): e is string => !!e),
          );
          return distinctCandidates.filter((e) => !existing.has(e));
        }),
        switchMap((newEmails) => {
          if (newEmails.length === 0) {
            return of(null);
          }
          return this.participantDomain.inviteParticipants(projectId, newEmails);
        }),
        finalize(() => this.isSubmitting.set(false)),
      )
      .subscribe({
        next: () => {
          this.router.navigate(['/projects', projectId, 'participants'], {
            state: { showLoading: true },
          });
        },
        error: () => {
          this.apiError.set('Failed to send invitations.');
        },
      });
  }
}
